import type {
  ContextMenuElementType,
  ContextMenuType,
  TabElementBehavior,
  TabElementProperty,
  TabInfo,
  WorkArea,
} from '@core/core/types'
import type { TabType } from '@library/model'
import { ObjectEntries } from 'common/helpers'
import type {
  BaseObject,
  DomainType,
  ProjectElementInfo,
  ProjectElementPropertyInfo,
  TabElementInfo,
} from 'common/types'

import { convertToApiElementType } from '../converters'
import type {
  ContextMenu as ApiContextMenu,
  Element as ApiElement,
  ElementSettingInfo as ApiElementSettingInfo,
  ElementType as ApiElementType,
  ProjectElementInfo as ApiProjectElementInfo,
  SubMenuItem as ApiSubMenuItem,
  TabElementInfo as ApiTabElementInfo,
  TabElementProperty as ApiTabElementProperty,
  TabInfo as ApiTabInfo,
  TabType as ApiTabType,
  WorkAreaInfo as ApiWorkAreaInfo,
} from './__generated__/data-contracts'

export function convertTabInfo(tab: ApiTabInfo): TabInfo {
  return {
    id: tab.uid,
    workAreaId: tab.workAreaUid,
    type: convertTabType(tab.tabType),
    displayName: tab.name,
    orderNum: tab.orderNum,
    isVisible: tab.isVisible,
    domainType: convertDomainType(tab.tabSettings.domainType),
  }
}

function convertTabType(tabType: ApiTabType): TabType {
  switch (tabType) {
    case 'Window3D':
      return 'Window3D'
    case 'Window2D':
      return 'Window2D'
    case 'Interpretation':
      return 'Interpretation'
    case 'CrossPlot':
      return 'CrossPlot'
    case 'CorrelationScheme':
      return 'CorrelationScheme'
    case 'Table':
      return 'Table'
    case 'Chart':
      return 'Chart'
    case 'Map':
      return 'Map'
    case 'GeoModule':
      return 'GeoModule'
  }
}

function convertDomainType(domainType?: string): DomainType {
  switch (domainType) {
    case 'TWT':
      return 'TWT'
    case 'Z':
      return 'Z'
    default:
      return 'ANY'
  }
}

export function convertToProjectElementInfo(from: ApiProjectElementInfo): ProjectElementInfo {
  return {
    uid: from.uid,
    parentUid: from.parentUid ?? undefined,
    projectUid: from.projectUid,
    projectEntityUid: from.projectEntityUid ?? undefined,
    elementType: convertToBaseObject(from.elementType),
    name: from.name,
    orderNum: from.orderNum,
    icon: from.icon,
    state: from.state,
    countChild: from.countChild,
    children: [],
    properties: from.elementSettings.map(elem => ({
      propertyType: elem.propertyType,
      value: elem.value,
      elementId: elem.projectElementUid,
    })),
    domainType: from.domainType ?? undefined,
    disabled: from.disabledToSelect,
    tabElements: from.tabElements.map(convertToTabElementInfo),
  }
}

export function convertToApiProjectElementInfo(from: ProjectElementInfo): ApiProjectElementInfo {
  return {
    uid: from.uid,
    parentUid: from.parentUid,
    projectUid: from.projectUid,
    projectEntityUid: from.projectEntityUid,
    elementType: convertToApiElementType(from.elementType),
    name: from.name,
    orderNum: from.orderNum,
    icon: from.icon,
    state: from.state,
    countChild: from.countChild,
    disabledToSelect: from.disabled,
    domainType: from.domainType ?? 'Z',
    tabElements: [],
    elementSettings:
      from.properties?.map(fromItem => ({
        uid: from.uid,
        projectElementUid: fromItem.elementId,
        value: fromItem.value,
        propertyType: fromItem.propertyType,
      })) ?? [],
  }
}

export function convertToTabElementInfo(from: ApiTabElementInfo): TabElementInfo {
  return {
    tabId: from.tabUid,
    projectEntityId: from.projectEntityUid ? from.projectEntityUid : undefined,
    elementName: from.elementName,
    elementType: convertToBaseObject(from.elementType),
    projectElementId: from.projectElementUid,
    state: from.state,
    elementSettings: from.elementSettings?.map(convertElementSettings) ?? [],
  }
}

function convertElementSettings(from: ApiElementSettingInfo): ProjectElementPropertyInfo {
  return {
    propertyType: from.propertyType,
    value: from.value,
    elementId: from.projectElementUid,
  }
}

export function convertContextMenu(
  contextMenu: Record<ApiElementType, ApiContextMenu[]>,
): ContextMenuType[] {
  return ObjectEntries(contextMenu).map(([key, menu]) => ({
    id: convertToBaseObject(key),
    items: convertMenu(menu),
  }))
}

function convertMenu(contextMenuItems: ApiContextMenu[]): ContextMenuElementType[] {
  return contextMenuItems.map(item => ({
    label: item.menu.description,
    type: item.menu.name,
    subMenu: item.subMenu ? convertSubMenu(item.subMenu) : null,
  }))
}

function convertSubMenu(contextSubMenuItems: ApiSubMenuItem[]): ContextMenuElementType[] {
  return contextSubMenuItems.map(item => ({
    label: item.description,
    type: item.name,
  }))
}

export function convertToWorkArea(from: ApiWorkAreaInfo): WorkArea {
  return {
    id: from.uid,
    tabs: [],
    activeTabId: null,
  }
}

export function convertToBaseObject(from: ApiElementType): BaseObject {
  switch (from) {
    case 'Well':
      return 'well'
    case 'Field':
      return 'field'
    case 'Survey3DFolder':
      return 'cube'
    case 'Fault':
      return 'fault'
    case 'Horizon':
      return 'horizon'
    case 'Survey2DFolder':
      return 'survey2d'
    case 'Inclinometry':
      return 'inclinometry'
    case 'Marker':
      return 'marker'
    case 'GIS':
      return 'gis'
    case 'ILine':
      return 'iLine'
    case 'XLine':
      return 'xLine'
    case 'ZSlice':
      return 'Hline'
    case 'Polygon':
      return 'polygon'
    case 'Stick':
      return 'stick'
    case 'Map':
      return 'map'
    case 'WellBore':
      return 'wellBore'
    case 'Lithology':
      return 'lithology'
    case 'Saturation':
      return 'saturation'
    case 'VSP':
      return 'vsp'
    case 'Profile':
      return 'profile'
    case 'Interpretation2D':
      return 'interpretation2D'
    case 'Interpretation3D':
      return 'interpretation3D'
    case 'FaultsFolder':
      return 'faultsFolder'
    case 'Points':
      return 'points'
    case 'WellsFolder':
      return 'wellsFolder'
    case 'GISFolder':
      return 'gisFolder'
    case 'RIGISFolder':
      return 'rigisFolder'
    case 'MarkersFolder':
      return 'markersFolder'
    case 'SeismicFolder':
      return 'seismicFolder'
    case 'SeismicSurveys':
      return 'seismicSurveys'
    case 'Interpretation':
      return 'interpretation'
    case 'HorizonsFolder':
      return 'horizonsFolder'
    case 'SticksFolder':
      return 'sticksFolder'
    case 'PolygonsFolder':
      return 'polygonsFolder'
    case 'MapsFolder':
      return 'mapsFolder'
    case 'PointsFolder':
      return 'pointsFolder'
    case 'GISList':
      return 'gisList'
    case 'RIGISList':
      return 'rigisList'
    case 'VSPList':
      return 'vspList'
    case 'MarkerList':
      return 'markerList'
    case 'VelocityModel':
      return 'velocityModel'
    case 'VelocityModelsFolder':
      return 'velocityModelsFolder'
    case 'VirtualFolder':
      return 'virtualFolder'
    case 'SpeedLawsFolder':
      return 'speedLawsFolder'
    case 'None':
      return 'unknown'
  }
}

export function convertFromBaseObject(from: BaseObject): ApiElementType {
  switch (from) {
    case 'well':
      return 'Well'
    case 'field':
      return 'Field'
    case 'cube':
      return 'Survey3DFolder'
    case 'fault':
      return 'Fault'
    case 'horizon':
      return 'Horizon'
    case 'survey2d':
      return 'Survey2DFolder'
    case 'inclinometry':
      return 'Inclinometry'
    case 'marker':
      return 'Marker'
    case 'gis':
      return 'GIS'
    case 'iLine':
      return 'ILine'
    case 'xLine':
      return 'XLine'
    case 'Hline':
      return 'ZSlice'
    case 'polygon':
      return 'Polygon'
    case 'stick':
      return 'Stick'
    case 'map':
      return 'Map'
    case 'wellBore':
      return 'WellBore'
    case 'lithology':
      return 'Lithology'
    case 'saturation':
      return 'Saturation'
    case 'vsp':
      return 'VSP'
    case 'profile':
      return 'Profile'
    case 'interpretation2D':
      return 'Interpretation2D'
    case 'interpretation3D':
      return 'Interpretation3D'
    case 'faultsFolder':
      return 'FaultsFolder'
    case 'points':
      return 'Points'
    case 'wellsFolder':
      return 'WellsFolder'
    case 'gisFolder':
      return 'GISFolder'
    case 'rigisFolder':
      return 'RIGISFolder'
    case 'markersFolder':
      return 'MarkersFolder'
    case 'seismicFolder':
      return 'SeismicFolder'
    case 'seismicSurveys':
      return 'SeismicSurveys'
    case 'interpretation':
      return 'Interpretation'
    case 'horizonsFolder':
      return 'HorizonsFolder'
    case 'sticksFolder':
      return 'SticksFolder'
    case 'mapsFolder':
      return 'MapsFolder'
    case 'pointsFolder':
      return 'PointsFolder'
    case 'vspList':
      return 'VSPList'
    case 'markerList':
      return 'MarkerList'
    case 'velocityModel':
      return 'VelocityModel'
    case 'velocityModelsFolder':
      return 'VelocityModelsFolder'
    case 'virtualFolder':
      return 'VirtualFolder'
    case 'speedLawsFolder':
      return 'SpeedLawsFolder'
    case 'polygonsFolder':
      return 'PolygonsFolder'
    case 'gisList':
      return 'GISList'
    case 'rigisList':
      return 'RIGISList'
    case 'unknown':
      return 'None'
    default:
      throw new Error(`Unknown element type: ${from}`)
  }
}

export function convertToTabElementProperties(
  fromElements: Record<string, ApiElement>,
  fromTabElements: Record<string, ApiTabElementProperty>,
): TabElementProperty[] {
  const properties: TabElementProperty[] = []

  for (const tabElementKey in fromTabElements) {
    const tabElement = fromTabElements[tabElementKey]
    const behaviors: TabElementBehavior[] = []

    for (const behavior of tabElement.elementBehavior) {
      for (const elementKey in fromElements) {
        const element = fromElements[elementKey]
        if (element.elementType === behavior.elementType) {
          behaviors.push({
            elementType: convertToBaseObject(element.elementType),
            category: element.category,
            selectionType: behavior.selectionType,
          })
          break
        }
      }
    }
    properties.push({ tabType: tabElementKey as TabType, behaviors })
  }

  return properties
}
